import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { MessageService } from 'primeng/api'
import { forkJoin } from 'rxjs'

import {
  BLACKLIST_69B_STATUS_LABELS,
  COUNTERPARTY_WINDOW_LABELS,
  formatPesos,
  formatPesosCompact,
  formatRiskValue,
  formatShortDate,
  labelOf,
  PillTone,
  RISK_KEYS,
  RISK_LABELS,
  SEVERITY_TONE,
} from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { AnalysisView, BackgroundCheck, SnapshotContext, SnapshotSection } from '@data/interfaces/verification-snapshot.interface'
import { ApiVerificationSnapshotService } from '@data/services/modules/verification-snapshot/api-verification-snapshot.service'
import { BlacklistRow, blacklistRowsFromPhoto, EkatenaLists, ListBadge, listBadgesFrom } from './analysis-photo.util'
import { sectionUiState, SnapshotUiState } from './tabs/snapshot-state/snapshot-state.component'
import { CiecTranslateService, ParsedRationale } from '../../services/ciec-translate.service'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { RiskScorecardStore } from './tabs/risk-scorecard.store'
import { SnapshotFiles } from './tabs/snapshot-tab.base'

/**
 * Ventana tras la cual una extracción que sigue en `pending`/`processing` se
 * considera colgada y deja de bloquear la UI.
 *
 * El backend dispara la extracción con un evento **en memoria del pod**
 * (`syntage.extraction.started`) y el HTTP que la originó ya respondió. Si el pod
 * se reinicia a media extracción — un deploy basta — nadie marca el registro como
 * `failed` y queda en `processing` para siempre. Sin esta ventana el panel se
 * clavaba en "Procesando" con el botón de análisis apagado y sin explicación.
 *
 * 10 min espeja el mismo umbral con el que el backend reutiliza una verificación
 * `pending` (`client-verification.service.ts` · `createPending`), y deja margen de
 * sobra sobre la duración real de una extracción sana (1–2 min).
 */
export const CIEC_EXTRACTION_STALE_MS = 10 * 60 * 1000

/**
 * `true` mientras una extracción sigue viva: está en `pending`/`processing` y arrancó
 * dentro de la ventana. Es el ÚNICO lugar donde se decide qué cuenta como "en curso"
 * — lo consumen este componente y `customer-detail`, que si no duplicarían la misma
 * aritmética de fechas y podrían desincronizarse en silencio.
 *
 * Sin `extractionStartedAt` no se puede fechar y la damos por no viva: permitir un
 * reintento de más es mejor que dejar la pantalla bloqueada para siempre.
 */
export function isCiecExtractionAlive(verification: any): boolean {
  const status = verification?.extractionStatus
  if (status !== 'pending' && status !== 'processing') return false
  const started = new Date(verification?.extractionStartedAt ?? NaN).getTime()
  if (isNaN(started)) return false
  return Date.now() - started <= CIEC_EXTRACTION_STALE_MS
}

/** Un indicador de riesgo de Syntage (`/insights/risks`). */
export interface RiskItem {
  key: string
  label: string
  value: string
  risky: boolean
}

/**
 * Secciones del detalle de Análisis, apiladas en este orden; «Decisión» solo en el arrendamiento. Los ratios de Syntage
 * ya no van aquí: se movieron a la pestaña Ratios (D-42).
 */
export type AnalysisSection = 'finance' | 'concentration' | 'offer' | 'decision'

/** Las pestañas de la calculadora de riesgo, en el orden del Excel (D-41). */
export type RiskTab = 'flows' | 'statements' | 'ratios' | 'calculator'
const RISK_TABS: ReadonlyArray<RiskTab> = ['flows', 'statements', 'ratios', 'calculator']

/** Un año de la tabla de Finanzas: lo facturado (serie de ingresos) junto al comparativo de ingresos y gastos. */
export interface FinanceRow {
  year: number
  label: string
  billed: number | null
  delta: number | null
  income: number | null
  expenses: number | null
  margin: number | null
}

/** Una barra de las gráficas compactas: alto en px y el texto de su `title`. */
export interface MiniBar {
  height: number
  title: string
}

/** Alto de las gráficas de Cash flow e Ingresos por ventas (maqueta F-158: 150 px). */
const FINANCE_CHART_HEIGHT = 150
/** Meses que se ven en cada gráfica compacta. */
const CASH_FLOW_MONTHS = 13
const SALES_MONTHS = 36
/** Puntos y alto máximo de la mini-gráfica de empleados. */
const EMPLOYEE_SPARK_POINTS = 8
const EMPLOYEE_SPARK_HEIGHT = 20
/** Alto mínimo de una barra: un mes en 0 o muy chico se sigue viendo como raya. */
const MIN_BAR_HEIGHT = 2

/** Alto en px de una barra a escala del valor más alto de su gráfica; nunca menos de `MIN_BAR_HEIGHT`. */
export function scaleBar(value: number, max: number, maxHeight: number): number {
  if (!(max > 0) || !(value > 0)) return MIN_BAR_HEIGHT
  return Math.max(MIN_BAR_HEIGHT, Math.round((value / max) * maxHeight))
}

/** Signo menos tipográfico y espacio antes del %, como las cifras de la maqueta (`−43.9 %`). */
export function formatPercent(value: number | null, decimals = 1): string {
  if (value === null || !Number.isFinite(value)) return '—'
  return `${value < 0 ? '−' : ''}${Math.abs(value).toFixed(decimals)} %`
}

/** Una variación lleva siempre su signo: `+13.1 %` sube, `−43.9 %` baja. Sin dato, «—». */
export function formatDelta(value: number | null, decimals = 1): string {
  if (value === null || !Number.isFinite(value)) return '—'
  return `${value > 0 ? '+' : ''}${formatPercent(value, decimals)}`
}

/**
 * El mes `AAAA-MM` de una fecha de Syntage. Casi siempre llega como ISO, pero los flujos de efectivo de `result` traen
 * fechas que no empiezan así (en K1 la gráfica rotulaba «2025/Au»): esas se intentan leer como fecha y, si no se puede,
 * se quedan con sus primeros 7 caracteres, como se agrupaban antes, para no perder el mes.
 */
export function monthKey(value: unknown): string {
  const text = String(value ?? '')
  if (/^\d{4}-\d{2}/.test(text)) return text.slice(0, 7)
  const date = new Date(text)
  return isNaN(date.getTime()) ? text.slice(0, 7) : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Suma por año la serie mensual de ingresos y deja los últimos `years` años (ADR-0002: el total anual
 * se deriva en el front a partir de los primitivos). El año en curso va incompleto: la tabla lo marca.
 * Acepta las dos formas: la de `result` (`{ date, mxnAmount }`) y la de la foto (`{ d: 'AAAA-MM', a }`).
 */
export function annualBillingFromSeries(series: unknown, years = 3): Array<{ year: number; totalFacturado: number }> {
  if (!Array.isArray(series)) return []
  const byYear = new Map<number, number>()
  for (const point of series) {
    const year = Number(String(point?.date ?? point?.d ?? '').slice(0, 4))
    if (!year) continue
    byYear.set(year, (byYear.get(year) ?? 0) + (Number(point?.mxnAmount ?? point?.metric ?? point?.a ?? 0) || 0))
  }
  return [...byYear.entries()]
    .sort(([a], [b]) => a - b)
    .slice(-years)
    .map(([year, total]) => ({ year, totalFacturado: Math.round(total) }))
}

/** El `total` del punto más reciente de una serie `{ date, total }`, o `null` si no hay serie. */
export function latestPoint(series: unknown): number | null {
  if (!Array.isArray(series) || series.length === 0) return null
  const latest = [...series].sort((a, b) => String(a?.date ?? '').localeCompare(String(b?.date ?? ''))).pop()
  return toFiniteNumber(latest?.total ?? latest?.metric)
}

/** Escala del Syntage Score: sus bandas solo existen sobre ella. */
export const SYNTAGE_SCORE_SCALE = 1000

/**
 * La escala en la que CALIFICA cada proveedor, para pintar su score (F-158, capacitación): Syntage y Ekatena van de 0 a
 * 1000. Un proveedor que no esté aquí se pinta sobre la escala guardada en la cobertura.
 */
export const PROVIDER_SCORE_SCALES: Record<string, number> = {
  syntage: SYNTAGE_SCORE_SCALE,
  ekatena: 1000,
}

/**
 * Base común sobre la que están calibrados los tramos y las calificaciones del algoritmo (la misma
 * `CANONICAL_SCORE_SCALE` de la api). También es la escala de los análisis anteriores a que la cobertura
 * guardara la suya: todos salieron de Syntage cuando calificaba sobre 850.
 */
export const CANONICAL_SCORE_SCALE = 850

/**
 * El score crudo del proveedor que hizo el análisis, para los análisis sin `coverage.scoreRaw`: la entrada de
 * `scores[]` cuya `source` es ese proveedor y, si no está, el sello `verification.syntage.score`.
 *
 * Nunca `scores[0]`: Syntage también entrega ahí el score de Buró (`source: 'buro_de_credito'`), a veces
 * primero, que es otro modelo sobre otra escala. Es la misma regla que aplica la api al calcular (F-158).
 */
export function providerScoreFrom(scores: unknown, provider: string): unknown {
  const own = (Array.isArray(scores) ? scores : []).find((item: any) => String(item?.source ?? '').toLowerCase() === provider)
  return own?.score
}

/** Bandas del Syntage Score 0-1000: 0-249 Bajo · 250-499 Regular · 500-749 Bueno · 750-1000 Excelente. */
export function syntageScoreBand(score: number): string {
  if (score >= 750) return 'Excelente'
  if (score >= 500) return 'Bueno'
  if (score >= 250) return 'Regular'
  return 'Bajo'
}

/**
 * Aplana `black_lists` (`{ issued[], received[] }` con `{ invoices, taxpayer: { rfc, name, blacklistStatus } }`).
 * La plantilla leía `description/note/date`, campos que no existen, y pintaba «—» en todas las filas.
 */
export function blacklistRowsFrom(blackLists: any): BlacklistRow[] {
  const rows = (items: unknown, direction: BlacklistRow['direction']): BlacklistRow[] =>
    (Array.isArray(items) ? items : []).map((item: any) => ({
      direction,
      name: item?.taxpayer?.name || '—',
      rfc: item?.taxpayer?.rfc || '—',
      status: labelOf(BLACKLIST_69B_STATUS_LABELS, item?.taxpayer?.blacklistStatus),
      invoices: Number(item?.invoices ?? 0) || 0,
    }))
  return [...rows(blackLists?.issued, 'Emitida'), ...rows(blackLists?.received, 'Recibida')]
}

/**
 * `riesgos` es un objeto `{ <llave>: { value, risky } | null }`. La plantilla lo trataba como arreglo
 * (`riesgos?.length`) y el bloque no se pintaba nunca. Las 13 llaves conocidas van en su orden; una
 * llave nueva se muestra con su nombre crudo en vez de tirarse. En la foto, las llaves nuevas llegan
 * agrupadas en `extra` (reductor `risks.reducer.ts`) y se aplanan aquí.
 */
export function riskItemsFrom(risks: any): RiskItem[] {
  if (!risks || typeof risks !== 'object' || Array.isArray(risks)) return []
  const { extra, ...known } = risks
  const all = extra && typeof extra === 'object' ? { ...known, ...extra } : known
  const order = (key: string) => {
    const i = (RISK_KEYS as readonly string[]).indexOf(key)
    return i === -1 ? RISK_KEYS.length : i
  }
  return Object.entries(all)
    .sort(([a], [b]) => order(a) - order(b))
    .map(([key, item]: [string, any]) => ({
      key,
      label: labelOf(RISK_LABELS, key),
      value: formatRiskValue(key, item?.value),
      risky: item?.risky === true,
    }))
}

/**
 * Vista visual presentacional del análisis CIEC/Syntage (ADR-0031).
 *
 * Se consume desde dos lugares:
 *   - `customer-detail` (pestaña "Análisis Fiscal CIEC"): `mode='client'`, recibe la
 *     `ClientVerification` vigente del cliente. Refresh permitido siempre que no haya
 *     extracción `pending`.
 *   - `lease-algorithm` (pestaña "Algoritmo" del lease): `mode='lease-snapshot'`, recibe
 *     el snapshot `lease.verification`. Refresh permitido solo en estados mutables del
 *     lease (`Pendiente | Aprobado`); congelado en `Activo | Liquidado | Cancelado`.
 *
 * Sin HTTP: emite `(refresh)` y el padre dispara la llamada al backend correspondiente
 * (`POST /clients/:id/verify-ciec` o `POST /leases/:id/verify-ciec`).
 */
@Component({
  selector: 'app-ciec-analysis-view',
  templateUrl: './ciec-analysis-view.component.html',
  styleUrls: ['./ciec-analysis-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  //* Una sola petición de la calculadora para sus cuatro pestañas, mientras viva esta vista (plan v4.2 §8b.3).
  providers: [RiskScorecardStore],
})
export class CiecAnalysisViewComponent implements OnInit, OnChanges {
  @Input() verification: any | null = null
  @Input() mode: 'client' | 'lease-snapshot' = 'client'
  @Input() canRefresh = true
  @Input() refreshing = false
  @Input() refreshDisabledReason?: string
  /**
   * Un análisis de Ekatena que **ya existe** para este RFC, si lo hay.
   *
   * Lo resuelve el padre —este componente es presentacional y no pega a la red— y llega `null`
   * cuando no hay ninguno, que es lo normal. Preguntarlo es gratis: el reporte ya fue pagado por
   * quien lo corrió en el panel de Ekatena, y esta ficha solo lo hace visible.
   */
  @Input() ekatenaReport: any | null = null
  /** `true` mientras el padre averigua si hay algo disponible. */
  @Input() ekatenaBuscando = false
  /** `true` mientras se importa, para que el botón no se pueda pulsar dos veces. */
  @Input() ekatenaImportando = false
  /**
   * F-158 · Dónde pedir la foto de Syntage para las sub-pestañas Buró y deuda / Legal y corporativo.
   * Lo arma el padre (`lease-algorithm` con `scope: 'lease'`, `customer-detail` con `scope: 'client'`);
   * sin contexto esas dos pestañas no se pintan.
   */
  @Input() snapshotContext: SnapshotContext | null = null
  /**
   * Plan v4.3 §9.3 (A-16) · Sub-pestaña con la que abre, por clave: la bandeja de Legal enlaza «Ver en la foto» a la
   * sección de cada señal. Se aplica una sola vez, cuando ya se sabe qué pestañas se ven.
   */
  @Input() initialSubtab: string | null = null
  private initialSubtabApplied = false

  @Output() refresh = new EventEmitter<void>()
  /** El usuario decidió traer el análisis que ya existe en Ekatena. */
  @Output() importEkatena = new EventEmitter<void>()

  /**
   * ADR-0032 — emitido cuando el usuario interno aprieta "Solicitar CIEC al cliente".
   * El padre (`customer-detail`) escucha y abre el modal de magic link. Solo válido
   * en `mode='client'`; en `lease-snapshot` el botón nunca se renderiza.
   */
  @Output() requestCiec = new EventEmitter<void>()

  /**
   * ADR-0032 — emitido cuando el usuario interno aprieta "Ingresar CIEC" (empty state).
   * El padre (`customer-detail`) abre un modal para capturar la CIEC a mano y corre la
   * extracción con ella (en vez de depender de que la meta el cliente). Solo en `mode='client'`.
   */
  @Output() enterCiec = new EventEmitter<void>()

  /**
   * Sub-pestaña abierta: 0 Análisis, luego Buró y deuda y Legal y corporativo (con `leases.view`) y luego Flujos,
   * Estados financieros, Ratios y Calculadora (con `risk-scorecard.view`). Los índices dependen de cuáles se ven.
   */
  subtab = 0
  /** Detalle de la fila «Score crediticio» del cálculo (aspectos positivos y negativos). */
  scoreDetailOpen = false

  // Series mensuales de las gráficas compactas (antes Chart.js): se derivan de `result` igual que antes.
  cashFlowChart: { labels: string[]; inflow: number[]; outflow: number[] } | null = null
  salesRevenueChart: { labels: string[]; data: number[] } | null = null
  cashBars: Array<{ inflow: MiniBar; outflow: MiniBar }> = []
  salesBars: MiniBar[] = []
  /** Primer y último mes de cada gráfica, para su eje. */
  cashAxis: { from: string; to: string } | null = null
  salesAxis: { from: string; to: string } | null = null
  /** Los últimos meses de empleados para la mini-gráfica de su casilla. */
  employeesSpark: MiniBar[] = []
  /** Una fila por año (Facturado, Δ, Ingresos, Gastos, Margen) y el acumulado. */
  financeRows: FinanceRow[] = []
  financeTotal: FinanceRow | null = null
  /** Mes del último dato del año más reciente, si ese año va incompleto (`sep`). */
  billingThrough: string | null = null
  /** El año anterior al más reciente tiene sus 12 meses en la serie. */
  previousYearComplete = false

  readonly compact = formatPesosCompact
  readonly pesos = formatPesos
  readonly percent = formatPercent
  readonly delta = formatDelta
  readonly shortDate = formatShortDate

  // Derived getters reactivos a `verification`
  result: any = null
  syntageData: any = null
  /** Los ratios de Syntage que guardó el análisis: la pestaña Ratios los usa cuando la calculadora no tiene foto. */
  financialRatios: any = null
  scores: any[] = []
  customerConcentration: any[] = []
  supplierConcentration: any[] = []
  facturacion3anios: any[] = []
  facturacionComparativa: any[] = []
  /**
   * PEP · Sanciones · OFAC, ya resueltos de la foto, de Ekatena o de la bandera vieja
   * (`listBadgesFrom`, F-158).
   *
   * ! Una bandera en `false` NO significa «limpio»: en los análisis de Syntage
   * ! `syntage_listas_negras_indicadores` ni siquiera llega (la lista blanca de `verification.service.ts`
   * ! lo descarta) y aquí se leía como «Limpio» siempre. Sin dato consultado, lo honesto es «No consultado».
   */
  listBadges: ListBadge[] = []
  /** Contrapartes en el 69-B (`result.black_lists`), ya aplanadas para la tabla. */
  blacklistRows: BlacklistRow[] = []
  /** Los indicadores de `result.riesgos`, que es un OBJETO por llave y no un arreglo. */
  riskItems: RiskItem[] = []
  /** Último punto de la serie de empleados; `null` si no hay serie. Un 0 es un dato, no un vacío. */
  employeesCurrent: number | null = null

  // ─── F-158 · Foto de Syntage para el bloque Análisis ──────────────────────
  /** La vista `analysis` de la foto; `null` sin foto o con un análisis anterior a ella. */
  photo: AnalysisView | null = null
  /** El background check (llega en la vista `legal`): de ahí salen PEP, Sanciones y OFAC. */
  photoBackground: SnapshotSection<BackgroundCheck> | null = null
  /** La foto no se pudo cargar: se avisa y se pinta lo guardado en el análisis. */
  photoLoadFailed = false
  /**
   * La foto se está pidiendo. Mientras tanto listas, 69-B y riesgos pintan «Consultando…»: lo que trae
   * `result` cambiaría al llegar la foto («No consultado» que pasa a «Sin hallazgos», 0 contrapartes que
   * pasan a 5), y un valor que se ve definitivo y luego cambia es peor que esperar un segundo.
   */
  photoLoading = false
  /** Estado de la sección 69-B de la foto cuando no hay filas que pintar (vacía, pendiente…). */
  blacklistState: SnapshotUiState | null = null
  /** Estado de la sección de riesgos de la foto cuando no hay indicadores que pintar. */
  risksState: SnapshotUiState | null = null
  /** Nota de ventana del detalle 69-B; solo con foto, que es la que declara su ventana. */
  blacklistWindowLabel: string | null = null
  /** Qué foto se pidió por última vez, para no repetir la petición en cada cambio de entrada. */
  private photoKey: string | null = null

  // Para el progress bar del score, sobre la escala del proveedor
  //* El score **como lo dio el proveedor**, en su propia escala. Es lo que se pinta.
  syntageScore = 0
  /**
   * Calificación A-D **del algoritmo** (`result.grade`). Va aparte de la banda del proveedor: un 556
   * de Syntage es «Bueno» en su escala y puede ser C para el algoritmo, y mezclarlos confunde (F-158).
   */
  algorithmGrade = 'N/A'
  /** Banda de Syntage (Bajo · Regular · Bueno · Excelente), solo para su escala de 1000. */
  scoreBand: string | null = null
  scoreProgress = 0 // %
  /**
   * Escala con la que se calculó ESTE análisis (`coverage.scoreScale`). Syntage y Ekatena califican sobre
   * 1000; los análisis anteriores a que la cobertura la guardara se leen sobre 850.
   *
   * ! No es cosmético. Los umbrales A/B/C/D y los tramos de tasa están calibrados sobre 850, así
   * ! que clasificar un score de 1000 con ellos lo subiría de grado sin motivo. Por eso hay dos
   * ! valores separados: `syntageScore` es lo que se ENSEÑA —crudo, del proveedor— y
   * ! `scoreCanonico` es lo que se USA para clasificar, ya convertido a la base común.
   */
  scoreEscala = CANONICAL_SCORE_SCALE
  scoreCanonico = 0
  /**
   * La escala que se PINTA: la del proveedor (`PROVIDER_SCORE_SCALES`). Puede no coincidir con `scoreEscala`: los
   * análisis de la configuración v2 del algoritmo guardaron 850 aunque Syntage califique sobre 1000. Solo cambia lo que
   * se enseña; la calificación A-D es la guardada y el respaldo canónico sigue con `scoreEscala`.
   */
  scoreEscalaProveedor = CANONICAL_SCORE_SCALE

  // Para la stat card "Capacidad estimada"
  maxOfferAmount = 0
  minOfferAmount = 0
  capacityProgress = 0 // % de min sobre max (visual)

  // Para el badge de vigencia (mode='client')
  validityHint = ''
  isExpired = false
  needsRefresh = false

  // Fase E · rationales parseados a estructura ParsedRationale
  parsedExplanation: ParsedRationale[] = []
  // Fase F · separación para layout 2-col en bloque #12
  capacityRationale: ParsedRationale | null = null
  mainRationales: ParsedRationale[] = []

  private snapshotApi = inject(ApiVerificationSnapshotService)
  private messageService = inject(MessageService)
  private riskStore = inject(RiskScorecardStore)
  /** Contexto y fecha del análisis con que se pidió la calculadora: otra copia del mismo análisis no la vuelve a pedir. */
  private riskKey: string | null = null
  /**
   * `key` del `<p-toast>` propio del bloque Análisis: su aviso sale ahí y no en el `<p-toast>` sin `key` de la
   * página ni en el de una pestaña Buró/Legal abierta, que lo duplicarían.
   */
  readonly toastKey = 'ciec-analysis'
  /** El PDF de la opinión 32-D de la foto, con el mismo estado de copia y descarga que Buró y BIL. */
  readonly pdfs = new SnapshotFiles(() => this.photo, this.snapshotApi, this.messageService, this.cd, this.toastKey)

  constructor(
    public t: CiecTranslateService,
    private cd: ChangeDetectorRef,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.refreshDerived()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['verification']) {
      this.refreshDerived()
      // Charts dependen de result — solo construir si tenemos datos.
      if (this.result) {
        this.buildCashFlowChart()
        this.buildSalesRevenueChart()
      } else {
        this.cashFlowChart = null
        this.salesRevenueChart = null
      }
      this.buildFinanceBars()
      this.cd.markForCheck()
    }
    //* Otra sub-pestaña pedida con la vista ya abierta (un enlace dentro del mismo expediente): se vuelve a aplicar.
    if (changes['initialSubtab'] && !changes['initialSubtab'].firstChange) this.initialSubtabApplied = false
    if (changes['initialSubtab'] || changes['snapshotContext']) this.applyInitialSubtab()
    if (changes['verification'] || changes['snapshotContext']) {
      this.loadPhoto(false)
      //* Otro análisis u otro contexto (no otra copia del mismo): si una pestaña de la calculadora ya la pidió, se vuelve a pedir.
      const riskKey = this.snapshotContext ? `${this.snapshotContext.scope}:${this.snapshotContext.id}:${this.verification?.verifiedAt ?? ''}` : null
      if (riskKey !== this.riskKey) {
        this.riskKey = riskKey
        this.riskStore.invalidate(this.snapshotContext)
      }
    }
  }

  /** Reintento manual tras un fallo de carga de la foto. */
  retryPhoto(): void {
    this.loadPhoto(true)
  }

  /**
   * F-158 · Pide la foto de Syntage para el bloque Análisis, que tiene su propia vista.
   *
   * Se piden DOS vistas porque la API entrega `background` —de donde salen PEP, Sanciones y OFAC— solo
   * en la pestaña `legal`, no en `analysis`.
   *
   * Solo con un análisis exitoso, contexto y `leases.view` (el permiso de los endpoints): sin él el
   * backend respondería 403. Sin foto (`pre_snapshot`, `no_verification` o error) se queda lo que trae
   * `result`, que es lo de antes de F-158.
   */
  private loadPhoto(force: boolean): void {
    const ctx = this.snapshotContext
    if (!ctx || !this.isSuccess || !this.canViewSnapshotTabs) {
      this.photoKey = null
      this.photoLoading = false
      if (this.photo || this.photoBackground || this.photoLoadFailed) this.setPhoto(null, null, false)
      return
    }
    const key = `${ctx.scope}:${ctx.id}:${this.verification?.verifiedAt ?? ''}`
    if (!force && key === this.photoKey) return
    this.photoKey = key
    this.photoLoading = true
    this.cd.markForCheck()

    forkJoin([this.snapshotApi.getView(ctx, 'analysis'), this.snapshotApi.getView(ctx, 'legal')]).subscribe(([analysis, legal]) => {
      if (key !== this.photoKey) return
      this.photoLoading = false
      const failed = analysis.error || !analysis.data
      const photo = !failed && analysis.data?.state === 'ok' ? (analysis.data as AnalysisView) : null
      //* Sin la vista legal la foto se pinta igual; PEP, Sanciones y OFAC caen a «No consultado».
      const background = photo && !legal.error && legal.data?.state === 'ok' ? ((legal.data.sections['background'] as SnapshotSection<BackgroundCheck> | undefined) ?? null) : null
      this.setPhoto(photo, background, failed)
    })
  }

  private setPhoto(photo: AnalysisView | null, background: SnapshotSection<BackgroundCheck> | null, failed: boolean): void {
    this.photo = photo
    this.photoBackground = background
    this.photoLoadFailed = failed
    this.refreshDerived()
    this.cd.markForCheck()
  }

  onRefresh(): void {
    if (!this.canRefresh || this.refreshing) return
    this.refresh.emit()
  }

  /**
   * ADR-0032 — emite `requestCiec`. Solo si `canRequestCiec`. La UI ya lo
   * filtra con *ngIf, pero defendemos en el handler por consistencia.
   */
  onRequestCiec(): void {
    if (!this.canRequestCiec) return
    this.requestCiec.emit()
  }

  /**
   * ADR-0032 — emite `enterCiec` (empty state, "Ingresar CIEC"). Solo si `canMutate`
   * y `mode='client'`; en `lease-snapshot` el botón nunca se renderiza.
   */
  onEnterCiec(): void {
    if (!this.canMutate || this.mode !== 'client') return
    this.enterCiec.emit()
  }

  /**
   * ADR-0032 — `true` cuando tiene sentido mostrar el botón "Solicitar CIEC al
   * cliente" en el frontend interno. Decisión del usuario: solo si NO hay
   * verificación vigente (ausente, fallida o expirada). Una vez la verificación
   * está vigente y la CIEC quedó guardada en bóveda, el botón "Actualizar análisis"
   * reusa la CIEC almacenada y este botón se oculta.
   *
   * En `mode='lease-snapshot'` nunca aparece — el lease ya tiene su snapshot
   * congelado y la acción de "solicitar al cliente" pertenece al nivel cliente.
   */
  get canRequestCiec(): boolean {
    if (this.mode !== 'client') return false
    if (!this.verification) return true
    if (this.verification.status === 'missing') return true
    if (this.verification.status === 'failed') return true
    //* Una extracción colgada deja al cliente sin verificación utilizable, igual que
    //* una fallida. Sin esta rama el estado quedaba con `status='pending'` y el
    //* usuario perdía la opción de mandar el enlace — justo en el caso que más la necesita.
    if (this.isExtractionStale) return true
    if (this.isExpired) return true
    //* B-075: un análisis «exitoso» sin credencial del SAT no sirve; la salida es pedirle la CIEC.
    if (this.hasNoSatCredential) return true
    return false
  }

  /**
   * B-075 · Análisis que se dio por exitoso sin que la CIEC diera de alta una credencial del SAT: antes
   * del arreglo, una CIEC que el proveedor no aceptaba igual producía una verificación `success` en $0.
   *
   * ! Solo `source === 'verify-ciec-async'` (el enlace y el alta del canal). Las de `manual-backoffice`
   * ! y `revalidation` del legado siempre traen `credentialId: null` aunque sí tuvieran credencial, y
   * ! las de `ekatena-import` no usan una: marcarlas sería una alarma falsa.
   */
  get hasNoSatCredential(): boolean {
    const v = this.verification
    return this.mode === 'client' && v?.status === 'success' && !v?.credentialId && v?.source === 'verify-ciec-async'
  }

  get noSatCredentialNotice(): string {
    const amount = this.maxOfferAmount === 0 ? 'el $0 no refleja capacidad' : 'su monto no refleja capacidad'
    return `Este análisis se generó sin credencial del SAT: ${amount}. Solicita la CIEC.`
  }

  /**
   * RBAC — `true` si el usuario puede DISPARAR mutaciones de CIEC (ejecutar/
   * actualizar análisis, solicitar CIEC al cliente). Esas acciones pegan a:
   *   - mode='client'         → `POST /clients/:id/verify-ciec` | `/ciec-request` (requiere `clients.update`)
   *   - mode='lease-snapshot' → `POST /leases/:id/verify-ciec`                    (requiere `leases.update`)
   * Ocultamos los botones para roles read-only sin el permiso (ej. Legal en la
   * vista de cliente: tiene `leases.update` pero NO `clients.update`) → evita el
   * 403 + toast "Sin Permisos" al click. Permiso estático post-login, OK con OnPush.
   */
  get canMutate(): boolean {
    const moduleName = this.mode === 'client' ? 'clients' : 'leases'
    return this.auth.userHasAuthorization(moduleName, 'update')
  }

  /**
   * F-158 · Buró y deuda y Legal y corporativo solo con `leases.view` en los DOS contextos: es el
   * permiso de la pantalla Algoritmo. Con `clients.view` el Buró llegaría a Broker y MasterBroker.
   * Sin el permiso tampoco se montan, así que no salen llamadas que den 403.
   */
  get canViewSnapshotTabs(): boolean {
    return this.auth.userHasAuthorization('leases', 'view')
  }

  /**
   * Flujos, Estados financieros, Ratios y Calculadora (D-41): con `risk-scorecard.view` (Admin, Operaciones, Finanzas,
   * Seller y Legal, D-16) y con contexto. Sin el permiso no se montan, así que no salen llamadas que den 403.
   */
  get canViewRiskTabs(): boolean {
    return !!this.snapshotContext && this.auth.userHasAuthorization('risk-scorecard', 'view')
  }

  /** Abre la sub-pestaña pedida por clave (`analysis`, `bureau`, `legal` o una de la calculadora), si se ve. */
  private applyInitialSubtab(): void {
    const key = this.initialSubtab
    if (this.initialSubtabApplied || !key || !this.snapshotContext) return
    this.initialSubtabApplied = true
    const snapshotTabs = this.canViewSnapshotTabs
    if (key === 'bureau' && snapshotTabs) this.subtab = 1
    else if (key === 'legal' && snapshotTabs) this.subtab = 2
    else if ((RISK_TABS as ReadonlyArray<string>).includes(key)) this.openRiskTab(key as RiskTab)
    else this.subtab = 0
    this.cd.markForCheck()
  }

  /** Abre una pestaña de la calculadora; su índice depende de si Buró y Legal se ven. */
  openRiskTab(tab: RiskTab): void {
    if (!this.canViewRiskTabs) return
    const before = 1 + (this.canViewSnapshotTabs && this.snapshotContext ? 2 : 0)
    this.subtab = before + RISK_TABS.indexOf(tab)
    this.cd.markForCheck()
  }

  // ─── helpers para el template (público para *ngIf) ───────────────────────

  get isEmpty(): boolean {
    if (!this.verification) return true
    if (this.verification.status === 'missing') return true
    //* Una extracción colgada nunca produjo resultado, así que para el usuario
    //* equivale a "no hay análisis". Cae al empty state — que sí tiene los botones
    //* para reintentar — en vez de al bloque de éxito, que se pintaría vacío.
    if (this.isExtractionStale) return true
    return false
  }
  get isPending(): boolean {
    const s = this.verification?.extractionStatus
    if (s !== 'pending' && s !== 'processing') return false
    return !this.isExtractionStale
  }

  /** Extracción que quedó colgada: dice estar en curso pero ya no está viva. */
  get isExtractionStale(): boolean {
    const s = this.verification?.extractionStatus
    if (s !== 'pending' && s !== 'processing') return false
    return !isCiecExtractionAlive(this.verification)
  }

  /**
   * Nota extra del empty state. Distingue "nunca se corrió" de "se corrió y quedó
   * colgada", para que el usuario no crea que perdió un análisis que sí existía.
   */
  get emptyStateNote(): string {
    if (!this.isExtractionStale) return ''
    return 'Un intento anterior se quedó a medias y no llegó a completarse. Puedes volver a ejecutarlo.'
  }
  get isFailed(): boolean {
    return this.verification?.status === 'failed'
  }
  get isSuccess(): boolean {
    return this.verification?.status === 'success'
  }
  get failureReason(): string {
    return this.verification?.failureReason || this.verification?.result?.message || 'No se pudo completar la verificación.'
  }

  get heroSubtitle(): string {
    const parts: string[] = []
    if (this.verification?.rfc) parts.push(`RFC ${this.verification.rfc}`)
    if (this.verification?.verifiedAt) parts.push(`Verificado ${this.t.formatDate(this.verification.verifiedAt)}`)
    //! «Origen» decía DOS cosas distintas en la misma línea en cuanto entró la procedencia: cómo se
    //! disparó la verificación y de dónde salieron los datos. Son ejes ortogonales —una
    //! verificación pedida desde el panel puede traer datos de cualquier proveedor— así que cada
    //! uno lleva su propia etiqueta.
    //!
    //! Dice «Se pidió:» y **no** «Solicitado por:»: la verificación no guarda ningún usuario, solo
    //! el camino por el que entró. La segunda etiqueta prometería un nombre que el dato no tiene.
    if (this.verification?.source) parts.push(`Se pidió: ${this.t.translateSource(this.verification.source)}`)
    parts.push(`Datos de: ${this.providerLabel}`)
    return parts.join(' · ')
  }

  /** Severity del status pill principal del hero. */
  get statusSeverity(): 'success' | 'warning' | 'danger' | 'info' {
    if (this.isFailed) return 'danger'
    if (this.isPending) return 'info'
    if (this.hasNoSatCredential) return 'warning'
    if (this.needsRefresh) return 'warning'
    return 'success'
  }
  get statusLabel(): string {
    if (this.isFailed) return 'FALLIDA'
    if (this.isPending) return 'EN PROCESO'
    if (this.hasNoSatCredential) return 'SIN CREDENCIAL SAT'
    if (this.needsRefresh) return 'REQUIERE ACTUALIZACIÓN'
    return 'VIGENTE'
  }
  get statusIcon(): string {
    if (this.hasNoSatCredential) return 'pi pi-exclamation-triangle'
    return this.needsRefresh ? 'pi pi-clock' : 'pi pi-check-circle'
  }

  /** Estado del stat-card 3 (decisión) en mode='client'. */
  get clientStateIcon(): string {
    if (this.isExpired) return 'fa-circle-xmark'
    if (this.hasNoSatCredential) return 'fa-circle-exclamation'
    if (this.needsRefresh) return 'fa-circle-exclamation'
    return 'fa-circle-check'
  }
  get clientStateLabel(): string {
    if (this.isExpired) return 'EXPIRADA'
    if (this.hasNoSatCredential) return 'SIN CREDENCIAL SAT'
    if (this.needsRefresh) return 'REQUIERE ACTUALIZACIÓN'
    return 'VIGENTE'
  }
  /**
   * B-075 · Línea bajo el estado. Sin credencial del SAT el análisis no dice nada del cliente: «Vigente
   * hasta …» lo haría pasar por bueno. Si ya expiró, manda la expiración, igual que en la etiqueta.
   */
  get clientStateHint(): string {
    if (!this.isExpired && this.hasNoSatCredential) return 'No refleja capacidad: falta la credencial del SAT'
    return this.validityHint
  }
  get clientStateApproved(): boolean {
    return !this.isExpired && !this.needsRefresh && !this.hasNoSatCredential
  }

  /** Severity y label de la decisión (mode='lease-snapshot'). */
  get decisionSeverity(): 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
    const d = this.verification?.decision
    if (d === 'approved_in_range' || d === 'approved_below_range') return 'success'
    if (d === 'approved_with_difference') return 'warning'
    if (d === 'rejected_no_range') return 'danger'
    return 'secondary'
  }
  get decisionLabel(): string {
    return this.t.translateDecision(this.verification?.decision)
  }
  get decisionApproved(): boolean {
    const d = this.verification?.decision
    return d === 'approved_in_range' || d === 'approved_with_difference' || d === 'approved_below_range'
  }

  /** Top N de un array de concentración ordenado descendente por `share`. */
  topConcentration(arr: any[], n = 5): any[] {
    if (!Array.isArray(arr)) return []
    return [...arr].sort((a, b) => (Number(b?.share) || 0) - (Number(a?.share) || 0)).slice(0, n)
  }

  /** Margen % de un periodo del comparativo anual. */
  marginPercent(period: any): number | null {
    const ingresos = Number(period?.totalIncome ?? period?.netIncome ?? 0)
    const gastos = Number(period?.netExpensesPlusPayroll ?? period?.totalExpenses ?? period?.netExpenses ?? 0)
    if (ingresos <= 0) return null
    return ((ingresos - gastos) / ingresos) * 100
  }

  trackByIdx(idx: number): number {
    return idx
  }

  /**
   * `true` cuando Syntage devolvió un score utilizable. Si no, la stat-card
   * central renderiza un layout alternativo ("Sin score crediticio") en vez
   * del placeholder con `—` + `D` que confundía visualmente (Fase G·issue 2).
   */
  // ─── F-158 · sub-pestaña Análisis compacta ────────────────────────────────

  /**
   * Las secciones de Análisis que tienen algo que pintar. Van todas en la misma página, una debajo de otra con su
   * título (sin segmentado: decisión de la capacitación de F-158); una sin datos no se pinta.
   */
  get analysisSections(): AnalysisSection[] {
    const show: Record<AnalysisSection, boolean> = {
      finance: this.financeRows.length > 0 || this.cashBars.length > 0 || this.salesBars.length > 0,
      concentration: this.customerConcentration.length > 0 || this.supplierConcentration.length > 0 || this.photoLoading || this.blacklistRows.length > 0 || !!this.blacklistState,
      offer: this.parsedExplanation.length > 0,
      decision: this.mode === 'lease-snapshot' && !!this.verification?.decision,
    }
    return (['finance', 'concentration', 'offer', 'decision'] as const).filter((section) => show[section])
  }

  hasSection(section: AnalysisSection): boolean {
    return this.analysisSections.includes(section)
  }

  /** La fila del año más reciente, que alimenta las casillas de Facturación y Margen. */
  get latestFinance(): FinanceRow | null {
    return this.financeRows[0] ?? null
  }

  /** Lo que dice la mini-gráfica de empleados al lector de pantalla. */
  get employeesSparkLabel(): string {
    const last = this.employeesSpark[this.employeesSpark.length - 1]
    return last ? `Empleados de los últimos ${this.employeesSpark.length} meses; el más reciente, ${last.title}` : ''
  }

  /**
   * El tooltip de la ficha de calificación. Si el análisis se calificó con otra escala que la del proveedor, lo dice: la
   * calificación guardada sale de esa escala, no de la que se pinta.
   */
  get gradeTooltip(): string {
    const base = 'Calificación del algoritmo de Rendafin'
    if (this.scoreEscala === this.scoreEscalaProveedor) return base
    return `${base}. Este análisis se calificó con la escala de ${this.scoreEscala} de la configuración anterior del algoritmo; la calificación sale de esa escala.`
  }

  /**
   * Tono de la ficha de la calificación del algoritmo, con la taxonomía del sistema de diseño: A ok, B info, C warn,
   * D bad. Sin calificación (`N/A`), neutra.
   */
  get gradeTone(): PillTone {
    const tones: Record<string, PillTone> = { A: 'ok', B: 'info', C: 'warn', D: 'bad' }
    return tones[String(this.algorithmGrade).toUpperCase()] ?? 'neutral'
  }

  /** Tono de la pastilla de la decisión del arrendamiento. */
  get decisionTone(): PillTone {
    return SEVERITY_TONE[this.decisionSeverity]
  }

  /** El indicador 32-D de los riesgos, para su casilla en la franja. */
  get taxCompliance(): RiskItem | null {
    return this.riskItems.find((item) => item.key === 'taxCompliance') ?? null
  }

  /** Los indicadores de riesgo con los marcados primero, sin cambiar el orden dentro de cada grupo. */
  get sortedRiskItems(): RiskItem[] {
    return [...this.riskItems.filter((item) => item.risky), ...this.riskItems.filter((item) => !item.risky)]
  }

  get riskyCount(): number {
    return this.riskItems.filter((item) => item.risky).length
  }

  /** El HHI de clientes o de proveedores tal como lo da el indicador de riesgos, con su marca. */
  concentrationRisk(key: 'customerConcentration' | 'supplierConcentration'): RiskItem | null {
    return this.riskItems.find((item) => item.key === key) ?? null
  }

  /**
   * El veredicto de PEP · Sanciones · OFAC en una palabra. Mientras llega la foto, «Consultando…»: nunca «Sin
   * hallazgos» antes de saberlo. «Sin hallazgos» solo si las tres se consultaron y salieron limpias.
   */
  get listsVerdict(): { label: string; tone: PillTone } {
    if (this.photoLoading) return { label: 'Consultando…', tone: 'neutral' }
    const states = this.listBadges.map((badge) => badge.state)
    if (states.includes('found')) return { label: 'Con hallazgos', tone: 'bad' }
    if (states.includes('error')) return { label: 'Error al consultar', tone: 'bad' }
    if (states.includes('pending')) return { label: 'Pendiente en Syntage', tone: 'warn' }
    if (states.length && states.every((state) => state === 'clear')) return { label: 'Sin hallazgos', tone: 'ok' }
    return { label: 'No consultado', tone: 'neutral' }
  }

  /** Lo corto de cada lista para la casilla: «2/3» bases si salió limpia; si no, su estado. */
  listShort(badge: ListBadge): string {
    const bases = /\((\d+) de (\d+) bases\)/.exec(badge.text)
    return badge.state === 'clear' && bases ? `${bases[1]}/${bases[2]}` : badge.text
  }

  /** «bases» al final de la casilla cuando alguna lista se dio en bases consultadas. */
  get listsInBases(): boolean {
    return this.listBadges.some((badge) => badge.state === 'clear' && /de \d+ bases/.test(badge.text))
  }

  /** Tono del estatus 69-B de una contraparte: definitivo en rojo, presunto en ámbar. */
  blacklistTone(status: string): PillTone {
    if (status === BLACKLIST_69B_STATUS_LABELS['definitive']) return 'bad'
    if (status === BLACKLIST_69B_STATUS_LABELS['presumed']) return 'warn'
    return 'neutral'
  }

  /** Cuál de las 4 fuentes de la capacidad base es la que se eligió (la que iguala el máximo). */
  get chosenCapacitySource(): number {
    const cap = this.capacityRationale
    return cap?.inputs?.findIndex((input) => input.value === cap.output.value) ?? -1
  }

  /** Factores del cálculo (sin la capacidad base ni el compuesto) y lo que no se pudo leer, aparte. */
  get factorRows(): ParsedRationale[] {
    return this.mainRationales.filter((r) => r.kind !== 'composite' && r.kind !== 'unknown')
  }

  get compositeRationale(): ParsedRationale | null {
    return this.mainRationales.find((r) => r.kind === 'composite') ?? null
  }

  get rawRationales(): ParsedRationale[] {
    return this.mainRationales.filter((r) => r.kind === 'unknown')
  }

  /** Aspectos positivos y negativos de cada score que los trae: el detalle de la fila «Score crediticio». */
  get scoreBreakdowns(): Array<{ source: string; positives: any[]; negatives: any[] }> {
    return this.scores
      .map((score: any) => ({ source: this.t.translateScoreSource(score?.source), positives: score?.metadata?.breakdown?.performers ?? [], negatives: score?.metadata?.breakdown?.offenders ?? [] }))
      .filter((item) => item.positives.length || item.negatives.length)
  }

  get hasSyntageScore(): boolean {
    return this.syntageScore > 0
  }

  // ─── Procedencia: de qué proveedor salió ESTE análisis ────────────────────

  /**
   * Cómo se llama el proveedor que produjo el análisis.
   *
   * Cae a «Syntage» cuando no hay sello, y eso es correcto y no una suposición perezosa: la
   * procedencia se empezó a guardar el 2026-08-21, y **todo lo anterior salió de Syntage** porque
   * era el único que había.
   */
  get providerLabel(): string {
    return this.verification?.provider?.label || 'Syntage'
  }

  /**
   * F-158 · Los indicadores PEP / OFAC / sanciones de un análisis de Ekatena. El proveedor viaja en la
   * raíz de la verificación del cliente y dentro de `result` en el arrendamiento (`lease.verification`
   * no declara `provider`); `indicadoresImportados` lo escribe `ekatena-import.service.ts` en la cobertura.
   */
  private ekatenaLists(flags: EkatenaLists['flags'] | null): EkatenaLists | null {
    const provider = this.verification?.provider?.key ?? this.result?.provider?.key
    if (provider !== 'ekatena') return null
    const imported = (this.result?.coverage ?? this.verification?.dataQuality)?.indicadoresImportados
    return { flags: flags ?? {}, imported: Array.isArray(imported) ? imported : null }
  }

  /** Etiqueta del hero: el score sobre su escala y, si aplica, su banda. La calificación A-D va aparte. */
  get scoreTagLabel(): string {
    const band = this.scoreBand ? ` · ${this.scoreBand}` : ''
    return `${this.providerLabel} ${this.syntageScore} / ${this.scoreEscalaLabel}${band}`
  }

  /** El score se ENSEÑA en la escala del proveedor; convertirlo antes de mostrarlo sería mentir. */
  get scoreEscalaLabel(): string {
    return String(this.scoreEscalaProveedor)
  }

  /**
   * El score no vino del proveedor: se sustituyó por el supuesto de la configuración.
   *
   * ! Afecta al 73% de las verificaciones y hasta hoy **no se pintaba en ninguna pantalla**. Una
   * ! oferta con grado B respaldada por un score real y otra con grado B supuesto se veían
   * ! idénticas, y la diferencia pesa al llevar un caso al Comité.
   */
  get scoreEsSupuesto(): boolean {
    return (this.result?.coverage ?? this.verification?.dataQuality)?.scoreAssumed === true
  }

  // ─── El análisis que ya existe en Ekatena ─────────────────────────────────

  /**
   * Si vale la pena ofrecer la importación.
   *
   * Se exige que el reporte esté **completo y con CIEC válida**: uno a medias no tiene indicadores
   * con los que calcular nada, y ofrecerlo sería prometer algo que al importarlo no aparecería.
   */
  get hayEkatenaDisponible(): boolean {
    const r = this.ekatenaReport
    if (!r) return false
    if (r.percentageComplete !== 100) return false
    return r.validCiec !== false
  }

  /** Si este análisis YA salió de Ekatena, no hay nada que importar. */
  get yaEsDeEkatena(): boolean {
    return this.verification?.provider?.key === 'ekatena'
  }

  /**
   * Lo que hace valioso el aviso: el análisis de aquí corre con score supuesto y allá hay uno real.
   * Es el caso que más rinde y el que nadie podía ver, porque los dos números vivían en pantallas
   * distintas.
   */
  get ekatenaAportaScoreReal(): boolean {
    return this.hayEkatenaDisponible && Number(this.ekatenaReport?.score) > 0 && (this.scoreEsSupuesto || !this.hasSyntageScore)
  }

  /** Este análisis salió del respaldo porque el principal no pudo. */
  get vieneDeRespaldo(): boolean {
    return !!this.verification?.provider?.fellBackFrom
  }

  /** De qué proveedor se cayó, para poder decir por qué se acabó preguntando al otro. */
  get respaldoMotivo(): string | null {
    const p = this.verification?.provider
    if (!p?.fellBackFrom) return null
    const de = p.fellBackFrom === 'syntage' ? 'Syntage' : p.fellBackFrom
    const motivos: Record<string, string> = {
      SYNTAGE_UNAVAILABLE: 'no respondió',
      SYNTAGE_NO_DATA: 'no trajo datos suficientes',
      CIEC_REJECTED: 'el SAT rechazó la CIEC',
    }
    const causa = motivos[p.fallbackReason] || 'no pudo entregar el reporte'
    return `${de} ${causa}, así que el análisis se hizo con ${this.providerLabel}.`
  }

  /**
   * La oferta conservadora con el monto acotado a la máxima (B-056). Los análisis guardados antes del arreglo
   * del motor pueden traerla por encima de la máxima; la API la acota con la misma regla al leerla.
   */
  get conservativeOffer(): { amount: number; interestRate: number; termMonths: number } | null {
    const offer = this.result?.minOffer
    if (!offer) return null
    const max = Number(this.result?.maxOffer?.amount)
    const amount = Number(offer.amount)
    return Number.isFinite(max) && max >= 0 && amount > max ? { ...offer, amount: max } : offer
  }

  /**
   * Diferencia entre la oferta máxima y la conservadora. Útil para que el
   * comité dimensione "cuánto más" puede ofrecer en la máxima vs la mínima.
   * Devuelve `null` si falta alguna, si la conservadora es 0 o si no hay diferencia que mostrar.
   */
  get maxOfferDelta(): { amount: number; percent: number } | null {
    const max = Number(this.result?.maxOffer?.amount ?? 0)
    const min = Number(this.conservativeOffer?.amount ?? 0)
    if (!max || !min || min <= 0 || max <= min) return null
    return { amount: max - min, percent: ((max - min) / min) * 100 }
  }

  /** Pago mensual estimado de la oferta conservadora. */
  get minOfferMonthly(): number | null {
    const offer = this.conservativeOffer
    if (!offer) return null
    return this.t.estimateMonthlyPayment(offer.amount, offer.interestRate, offer.termMonths)
  }

  /** Pago mensual estimado de la oferta máxima. */
  get maxOfferMonthly(): number | null {
    const offer = this.result?.maxOffer
    if (!offer) return null
    return this.t.estimateMonthlyPayment(offer.amount, offer.interestRate, offer.termMonths)
  }

  // ─── private — derivaciones a partir de `verification` ───────────────────

  private refreshDerived(): void {
    this.result = this.verification?.result || null
    this.syntageData = this.verification?.syntageData || this.result || null

    // Ratios de Syntage: ya no se pintan en Análisis; la pestaña Ratios los usa si la calculadora no tiene foto (D-42).
    this.financialRatios = this.result?.financial_ratios || null

    // Scores
    this.scores = Array.isArray(this.result?.scores) ? this.result.scores : []

    // Concentración
    this.customerConcentration = Array.isArray(this.result?.syntage_customer_concentration) ? this.result.syntage_customer_concentration : []
    this.supplierConcentration = Array.isArray(this.result?.syntage_supplier_concentration) ? this.result.syntage_supplier_concentration : []

    // Facturación
    //* Se suma en el front a partir de la serie mensual (ADR-0002): `syntage_facturacion_ultimos_3_anios`
    //* lo calcula el backend pero la lista blanca lo descarta en los análisis de Syntage, así que la
    //* tabla no salía nunca. Los de Ekatena sí lo traen y no tienen serie: para ellos queda de respaldo.
    const photoSeries = this.photo?.sections.series?.status === 'complete' ? this.photo.sections.series.data : null
    const billing = annualBillingFromSeries(photoSeries?.salesRevenue ?? this.result?.syntage_sales_revenue?.data)
    this.facturacion3anios = billing.length ? billing : Array.isArray(this.result?.syntage_facturacion_ultimos_3_anios) ? this.result.syntage_facturacion_ultimos_3_anios : []
    this.facturacionComparativa = Array.isArray(this.result?.syntage_facturacion_comparativa_anual) ? this.result.syntage_facturacion_comparativa_anual : []

    // Listas negras
    const indicadores = this.result?.syntage_listas_negras_indicadores || null
    this.listBadges = listBadgesFrom({ background: this.photoBackground, ekatena: this.ekatenaLists(indicadores), legacyFlags: indicadores })

    //* Con foto, el detalle 69-B y los riesgos salen de sus secciones; una sección sin datos pinta su
    //* estado (vacía, pendiente…) en vez de esconder el bloque o caer a lo viejo.
    const blacklist = this.photo?.sections.blacklistedCounterparties
    this.blacklistState = this.photo ? sectionUiState(blacklist) : null
    this.blacklistRows = this.photo ? blacklistRowsFromPhoto(blacklist?.data) : blacklistRowsFrom(this.result?.black_lists)
    this.blacklistWindowLabel = this.photo && blacklist?.data ? labelOf(COUNTERPARTY_WINDOW_LABELS, blacklist.data.window) : null
    const risks = this.photo?.sections.risks
    this.risksState = this.photo ? sectionUiState(risks) : null
    this.riskItems = riskItemsFrom(this.photo ? risks?.data : this.result?.riesgos)

    // Empleados
    //* El último punto de la serie: `syntage_empleados` lo deriva el backend igual, pero la lista blanca
    //* lo descarta. Queda de respaldo para los análisis de Ekatena, que lo traen y no tienen serie.
    //* Con foto manda `summary.totalEmployees` y, si no vino, el último mes de su serie.
    const employees = this.result?.employees_by_dates ?? this.result?.syntage_employees_by_dates
    const summary = this.photo?.sections.summary?.status === 'complete' ? this.photo.sections.summary.data : null
    const photoEmployees = toFiniteNumber(summary?.totalEmployees) ?? (photoSeries?.employees.length ? toFiniteNumber(photoSeries.employees[photoSeries.employees.length - 1].a) : null)
    this.employeesCurrent = photoEmployees ?? latestPoint(employees) ?? toFiniteNumber(this.result?.syntage_empleados)
    //! La casilla y la mini-gráfica tienen que decir lo mismo. La cifra sale del resumen de Syntage
    //! (`summary.totalEmployees`) y la serie, de la nómina mes a mes, que el reductor de la API rellena con 0 en los
    //! meses sin dato (`series.ts` · `monthly`): en K2 el resumen da 11 y la serie trae ocho meses en 0. Si el último
    //! punto de la serie no es la cifra de la casilla, la mini-gráfica no se pinta.
    const employeeSeries = photoSeries?.employees.length ? photoSeries.employees.map((point) => ({ d: point.d, a: point.a })) : this.employeePoints(employees)
    const lastEmployees = employeeSeries.length ? employeeSeries[employeeSeries.length - 1].a : null
    this.employeesSpark = lastEmployees !== null && lastEmployees === this.employeesCurrent ? this.sparkOf(employeeSeries) : []

    this.buildFinanceRows(photoSeries?.salesRevenue ?? this.result?.syntage_sales_revenue?.data)
    this.buildFinanceBars()

    //* Score del proveedor. `coverage.scoreRaw` y `coverage.scoreScale` los escribe el backend con
    //* el valor **tal como lo dio el proveedor**; sin ellos (verificaciones anteriores a la cobertura)
    //* se toma la entrada de `scores[]` de ese proveedor o el sello `syntage.score`, nunca `scores[0]`.
    //* La cobertura viaja por dos caminos: dentro de `result` y como `dataQuality` en la raíz de la
    //* verificación —el backend la escribe en los dos—. Se leen ambos para no depender de cuál
    //* llegue completo en cada endpoint.
    const cobertura = this.result?.coverage ?? this.verification?.dataQuality
    const scoreProvider = String(cobertura?.scoreProvider ?? this.verification?.provider?.key ?? this.result?.provider?.key ?? 'syntage').toLowerCase()
    const crudo = Number(cobertura?.scoreRaw ?? 0) || 0
    const heredado = Number(providerScoreFrom(this.scores, scoreProvider) ?? this.verification?.syntage?.score ?? this.result?.syntage_score ?? 0) || 0

    this.scoreEscala = Number(cobertura?.scoreScale) > 0 ? Number(cobertura.scoreScale) : CANONICAL_SCORE_SCALE
    this.syntageScore = crudo > 0 ? crudo : heredado
    //! El convertido, NO el crudo, es el que clasifica. Un 963 sobre 1000 vale 818 sobre 850, y
    //! clasificarlo como 963 lo pondría en el tramo A sin merecerlo.
    this.scoreCanonico = this.scoreEscala === CANONICAL_SCORE_SCALE ? this.syntageScore : (this.syntageScore * CANONICAL_SCORE_SCALE) / this.scoreEscala
    this.algorithmGrade = this.result?.grade || this.verification?.syntage?.classification || this.t.classifyByScore(this.scoreCanonico)
    //! Las bandas son de Syntage y solo sobre su escala de 1000. Un score de Ekatena también va sobre
    //! 1000 pero no es el mismo instrumento, y un 850 viejo no tiene bandas propias: no se inventan.
    this.scoreEscalaProveedor = PROVIDER_SCORE_SCALES[scoreProvider] ?? this.scoreEscala
    this.scoreBand = this.scoreEscalaProveedor === SYNTAGE_SCORE_SCALE && scoreProvider === 'syntage' && this.syntageScore > 0 ? syntageScoreBand(this.syntageScore) : null
    this.scoreProgress = Math.min(100, Math.max(0, (this.syntageScore / this.scoreEscalaProveedor) * 100))

    // Capacidad estimada
    this.maxOfferAmount = Number(this.result?.maxOffer?.amount ?? this.result?.maxLoanAmount ?? 0) || 0
    this.minOfferAmount = Number(this.conservativeOffer?.amount ?? 0) || 0
    this.capacityProgress = this.maxOfferAmount > 0 ? Math.min(100, Math.max(0, (this.minOfferAmount / this.maxOfferAmount) * 100)) : 0

    // Vigencia
    const exp = this.verification?.expiresAt
    this.validityHint = this.t.formatValidityHint(exp)
    const expDate = exp ? new Date(exp) : null
    this.isExpired = !!expDate && expDate.getTime() < Date.now()
    this.needsRefresh = !!this.verification?.needsRefresh || this.isExpired

    // Fase E · rationales parseados
    this.parsedExplanation = this.t.parseOfferExplanation(this.result?.offerExplanation || [])
    // Fase F · split para 2-col layout (Capacity → derecha, resto → izquierda)
    this.capacityRationale = this.parsedExplanation.find((r) => r.kind === 'capacity') ?? null
    this.mainRationales = this.parsedExplanation.filter((r) => r.kind !== 'capacity')
  }

  private buildCashFlowChart(): void {
    const cashFlows: any[] = this.result?.['cash flows'] || []
    if (!Array.isArray(cashFlows) || cashFlows.length === 0) {
      this.cashFlowChart = null
      return
    }
    const grouped: Record<string, { inflow: number; outflow: number }> = {}
    for (const item of cashFlows) {
      const date = monthKey(item?.date)
      if (!date) continue
      if (!grouped[date]) grouped[date] = { inflow: 0, outflow: 0 }
      const type = String(item?.type || '').toLowerCase()
      const amount = Number(item?.mxnAmount ?? item?.amount ?? 0) || 0
      if (type === 'inflow' || type === 'income' || type === 'ingreso' || type === 'ingresos') {
        grouped[date].inflow += amount
      } else {
        grouped[date].outflow += Math.abs(amount)
      }
    }
    const labels = Object.keys(grouped).sort()
    this.cashFlowChart = { labels, inflow: labels.map((l) => grouped[l].inflow), outflow: labels.map((l) => grouped[l].outflow) }
  }

  private buildSalesRevenueChart(): void {
    const series: any[] = this.result?.syntage_sales_revenue?.data || []
    if (!Array.isArray(series) || series.length === 0) {
      // Fallback: cash flows inflows
      const cashFlows: any[] = this.result?.['cash flows'] || []
      const inflowsByDate: Record<string, { date: string; mxnAmount: number; transactions: number }> = {}
      for (const item of cashFlows) {
        if (String(item?.type || '').toLowerCase() !== 'inflow') continue
        const date = monthKey(item?.date)
        if (!date) continue
        if (!inflowsByDate[date]) inflowsByDate[date] = { date, mxnAmount: 0, transactions: 0 }
        inflowsByDate[date].mxnAmount += Number(item?.mxnAmount || 0) || 0
        inflowsByDate[date].transactions += Number(item?.transactions || 0) || 0
      }
      const sorted = Object.values(inflowsByDate)
        .filter((it) => it.mxnAmount > 0)
        .sort((a, b) => a.date.localeCompare(b.date))
      if (sorted.length === 0) {
        this.salesRevenueChart = null
        return
      }
      this.salesRevenueChart = { labels: sorted.map((it) => it.date), data: sorted.map((it) => it.mxnAmount) }
      return
    }
    // Si tenemos serie directa
    const labels = series.map((s: any) => monthKey(s?.date || s?.period))
    this.salesRevenueChart = { labels, data: series.map((s: any) => Number(s?.mxnAmount || s?.metric || 0) || 0) }
  }

  /**
   * Barras compactas de Cash flow (13 meses, ingresos y egresos) e Ingresos por ventas (36 meses), a 150 px. Con foto,
   * de su serie mensual (`AAAA-MM`, huecos en 0); sin foto, de lo guardado en `result`, como las gráficas de antes.
   */
  private buildFinanceBars(): void {
    const month = (label: string) => this.shortMonth(label)
    const photoSeries = this.photo?.sections.series?.status === 'complete' ? this.photo.sections.series.data : null
    const photoCash = photoSeries?.cashFlow?.length ? photoSeries.cashFlow : null
    const photoMonths = photoCash ? [...new Set(photoCash.map((point) => point.d))].sort() : []
    const cash = photoCash
      ? {
          labels: photoMonths,
          inflow: photoMonths.map((d) => photoCash.find((point) => point.d === d && point.t === 'inflow')?.a ?? 0),
          outflow: photoMonths.map((d) => Math.abs(photoCash.find((point) => point.d === d && point.t === 'outflow')?.a ?? 0)),
        }
      : this.cashFlowChart
    if (cash?.labels.length) {
      const start = Math.max(0, cash.labels.length - CASH_FLOW_MONTHS)
      const labels = cash.labels.slice(start)
      const inflow = cash.inflow.slice(start)
      const outflow = cash.outflow.slice(start)
      const max = Math.max(0, ...inflow, ...outflow)
      const height = (value: number) => scaleBar(value, max, FINANCE_CHART_HEIGHT)
      this.cashBars = labels.map((label, i) => ({
        inflow: { height: height(inflow[i]), title: `${month(label)} · ingresos ${formatPesos(inflow[i])}` },
        outflow: { height: height(outflow[i]), title: `${month(label)} · egresos ${formatPesos(outflow[i])}` },
      }))
      this.cashAxis = { from: month(labels[0]), to: month(labels[labels.length - 1]) }
    } else {
      this.cashBars = []
      this.cashAxis = null
    }
    const sales = photoSeries?.salesRevenue?.length ? { labels: photoSeries.salesRevenue.map((point) => point.d), data: photoSeries.salesRevenue.map((point) => point.a) } : this.salesRevenueChart
    if (sales?.labels.length) {
      const start = Math.max(0, sales.labels.length - SALES_MONTHS)
      const labels = sales.labels.slice(start)
      const data = sales.data.slice(start)
      const max = Math.max(0, ...data)
      this.salesBars = labels.map((label, i) => ({
        height: scaleBar(data[i], max, FINANCE_CHART_HEIGHT),
        title: `${month(label)} · ${formatPesos(data[i])}`,
      }))
      this.salesAxis = { from: month(labels[0]), to: month(labels[labels.length - 1]) }
    } else {
      this.salesBars = []
      this.salesAxis = null
    }
  }

  /** `AAAA-MM` → «sep 2025», como el resto de los meses de la pantalla. */
  private shortMonth(label: string): string {
    const match = /^(\d{4})-(\d{2})/.exec(label ?? '')
    if (!match) return label
    return new Date(Number(match[1]), Number(match[2]) - 1, 1).toLocaleDateString('es-MX', { year: 'numeric', month: 'short' }).replace(/\./g, '')
  }

  /** Puntos `{ d, a }` de la serie vieja de empleados (`{ date, total }`), en orden de fecha. */
  private employeePoints(series: unknown): Array<{ d: string; a: number }> {
    if (!Array.isArray(series)) return []
    return series
      .map((point: any) => ({ d: String(point?.date ?? ''), a: Number(point?.total ?? point?.metric ?? 0) || 0 }))
      .filter((point) => point.d)
      .sort((a, b) => a.d.localeCompare(b.d))
  }

  /** Los últimos 8 meses de empleados a escala del más alto; el último en rojo si es 0. */
  private sparkOf(points: Array<{ d: string; a: number }>): MiniBar[] {
    const last = points.slice(-EMPLOYEE_SPARK_POINTS)
    const max = Math.max(0, ...last.map((point) => point.a))
    return last.map((point) => ({
      height: scaleBar(point.a, max, EMPLOYEE_SPARK_HEIGHT),
      title: `${this.shortMonth(point.d)}: ${point.a}`,
    }))
  }

  /**
   * Una fila por año con lo facturado (serie de ingresos) y el comparativo anual (ingresos, gastos, margen), del más
   * reciente al más viejo, y el acumulado. Si el año más reciente va incompleto se dice hasta qué mes.
   */
  private buildFinanceRows(series: unknown): void {
    const comparison = new Map<number, any>()
    for (const period of this.facturacionComparativa) {
      const year = Number(String(period?.period ?? period?.year ?? '').slice(0, 4))
      if (year) comparison.set(year, period)
    }
    const billed = new Map<number, number>(this.facturacion3anios.map((row) => [Number(row?.year), Number(row?.totalFacturado ?? 0)]))
    const years = [...new Set([...billed.keys(), ...comparison.keys()])].filter((year) => !!year).sort((a, b) => b - a)

    const months = this.monthsByYear(series)
    const latest = years[0]
    const latestMonths = latest ? months.get(latest) : undefined
    this.billingThrough = latestMonths && latestMonths.size > 0 && latestMonths.size < 12 ? this.shortMonth(`${latest}-${String(Math.max(...latestMonths)).padStart(2, '0')}`).split(' ')[0] : null
    this.previousYearComplete = (months.get(latest - 1)?.size ?? 0) === 12

    this.financeRows = years.map((year) => {
      const period = comparison.get(year)
      const income = period ? (toFiniteNumber(period?.totalIncome ?? period?.netIncome) ?? null) : null
      const expenses = period ? (toFiniteNumber(period?.netExpensesPlusPayroll ?? period?.totalExpenses ?? period?.netExpenses) ?? null) : null
      const previous = billed.get(year - 1)
      const current = billed.get(year)
      return {
        year,
        label: year === latest && this.billingThrough ? `${year} (a ${this.billingThrough})` : String(year),
        billed: current ?? null,
        delta: current !== undefined && previous !== undefined && previous > 0 ? ((current - previous) / previous) * 100 : null,
        income,
        expenses,
        margin: period ? this.marginPercent(period) : null,
      }
    })
    const sum = (values: Array<number | null>) => (values.some((value) => value !== null) ? values.reduce<number>((total, value) => total + (value ?? 0), 0) : null)
    const income = sum(this.financeRows.map((row) => row.income))
    const expenses = sum(this.financeRows.map((row) => row.expenses))
    this.financeTotal =
      this.financeRows.length > 1
        ? {
            year: 0,
            label: 'Acumulado',
            billed: sum(this.financeRows.map((row) => row.billed)),
            delta: null,
            income,
            expenses,
            margin: income && income > 0 ? ((income - (expenses ?? 0)) / income) * 100 : null,
          }
        : null
  }

  /** Qué meses trae cada año en la serie de ingresos (`{ d: 'AAAA-MM' }` de la foto o `{ date }` de `result`). */
  private monthsByYear(series: unknown): Map<number, Set<number>> {
    const months = new Map<number, Set<number>>()
    for (const point of Array.isArray(series) ? series : []) {
      const match = /^(\d{4})-(\d{2})/.exec(String(point?.date ?? point?.d ?? ''))
      if (!match) continue
      const year = Number(match[1])
      if (!months.has(year)) months.set(year, new Set())
      months.get(year)!.add(Number(match[2]))
    }
    return months
  }
}
